import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Shield, 
  Users, 
  LogOut,
  LayoutDashboard,
  ShoppingBag,
  CreditCard,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const AdminTransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [skip, setSkip] = useState(0);
  const navigate = useNavigate();
  const limit = 20;

  const fetchTransactions = useCallback(async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
      if (status !== "all") params.append("status", status);
      
      const response = await axios.get(`${API}/admin/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/admin/login");
      } else {
        toast.error("Failed to load transactions");
      }
    } finally {
      setLoading(false);
    }
  }, [skip, status, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchTransactions();
  }, [fetchTransactions, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    navigate("/admin/login");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "success": return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
      case "pending": return "bg-amber-500/10 text-amber-400 border-amber-500/30";
      default: return "bg-red-500/10 text-red-400 border-red-500/30";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount / 100); // Convert from kobo
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Admin Navbar */}
      <nav className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-violet-400" />
              </div>
              <span className="text-lg font-bold text-white">Admin Portal</span>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to="/admin/dashboard">
            <Button variant="outline" className="btn-secondary">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
          <Link to="/admin/users">
            <Button variant="outline" className="btn-secondary">
              <Users className="w-4 h-4 mr-2" />
              Users
            </Button>
          </Link>
          <Link to="/admin/requests">
            <Button variant="outline" className="btn-secondary">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Service Requests
            </Button>
          </Link>
          <Link to="/admin/transactions">
            <Button className="bg-violet-600 hover:bg-violet-700 text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Transactions
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card className="card-dashboard mb-6">
          <CardContent className="p-4">
            <Select value={status} onValueChange={(v) => { setStatus(v); setSkip(0); }}>
              <SelectTrigger className="w-full sm:w-48 input-default">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              Transactions ({total})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No transactions found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Reference</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Type</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Plan</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Amount</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn) => (
                        <tr key={txn.reference} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                          <td className="py-4 px-4">
                            <span className="text-white font-mono text-sm">{txn.reference}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-slate-300 capitalize">{txn.payment_type}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-slate-300 capitalize">{txn.plan_id || '-'}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-white font-medium mono">{formatCurrency(txn.amount)}</span>
                          </td>
                          <td className="py-4 px-4">
                            <Badge className={`capitalize ${getStatusBadge(txn.status)}`}>
                              {txn.status}
                            </Badge>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-slate-400 text-sm">
                              {new Date(txn.created_at).toLocaleString()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
                  <p className="text-slate-400 text-sm">
                    Showing {skip + 1} - {Math.min(skip + limit, total)} of {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSkip(Math.max(0, skip - limit))}
                      disabled={skip === 0}
                      className="btn-secondary"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSkip(skip + limit)}
                      disabled={skip + limit >= total}
                      className="btn-secondary"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminTransactionsPage;
